import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  HeartHandshake,
  Home,
  Search,
  Inbox,
  Users2,
  MessageSquare,
  Briefcase,
  ShieldCheck,
  CalendarCheck,
  MapPin,
  ShoppingBag,
} from "lucide-react";
import { toast } from "sonner";
import CareMap from "./CareMap";
import {
  VIEW_USERS,
  MOCK_REQUESTS,
  MOCK_CONNECTIONS,
  MOCK_MESSAGES,
  MOCK_REVIEWS,
  MOCK_CATEGORIES,
  MOCK_REPORTS,
} from "./careData";
import { CareHome, SearchResults, CaregiverProfile, RequestCareModal } from "./Browse";
import CareDirectory from "./Directory";
import { RequestsScreen, ConnectionsScreen, MessagesScreen } from "./Inbox";
import LiveNow from "../LiveNow";
import { GoLiveButton } from "../modules/LiveEvents";
import { CaregivingHub } from "./Caregiving";
import { AdminPanel } from "./Admin";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CareConnections({ onBack, onHome, onMasterDash, currentUser, onSignOut, onEditAvatar, onOpenStore, onAvatarSync, initialTab }) {
  // RBAC lockdown: admin view is derived strictly from the server-verified role — master only, no client-side switching
  const roleMap = { master: "admin", caregiver: "caregiver" };
  const viewUser = VIEW_USERS[roleMap[currentUser?.role] || "family"];
  const [screen, setScreen] = useState(initialTab ? "find" : "home");
  const [caregivers, setCaregivers] = useState([]);

  // Live marketplace: real caregiver profiles only — no demo data after go-live
  const loadCaregivers = () => {
    fetch(`${API}/api/public/showcase`)
      .then((r) => (r.ok ? r.json() : { caregivers: [] }))
      .then((d) =>
        setCaregivers(
          (d.caregivers || []).map((c) => ({
            id: c.id,
            name: c.name,
            title: c.detail || "Caregiver",
            photo: c.photo,
            status: "active",
            available: true,
            services: c.services || [],
            hourlyRate: c.hourly_rate || 0,
            rateMax: 0,
            rating: c.rating || 0,
            reviewCount: 0,
            yearsExperience: 0,
            distanceMiles: 0,
            verified: c.verification_status === "verified",
          }))
        )
      )
      .catch(() => {});
  };
  useEffect(() => {
    loadCaregivers();
  }, []);

  // Load the signed-in caregiver's own published profile (persisted server-side)
  useEffect(() => {
    if (currentUser?.role !== "caregiver") return;
    fetch(`${API}/api/caregiver/profile`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d?.profile) return;
        const p = d.profile;
        setMyCaregiverProfile({
          ...p,
          id: currentUser.id,
          yearsExperience: p.years_experience,
          experienceDetails: p.experience_details,
          radiusMiles: p.radius_miles,
          hourlyRate: p.hourly_rate,
          rateMax: p.rate_max,
          verificationStatus: d.verification_status,
        });
      })
      .catch(() => {});
  }, [currentUser?.role]);
  const [requests, setRequests] = useState(MOCK_REQUESTS);
  const [connections, setConnections] = useState(MOCK_CONNECTIONS);
  const [threads, setThreads] = useState(MOCK_MESSAGES);
  const [reviews, setReviews] = useState(MOCK_REVIEWS);
  const [categories, setCategories] = useState(MOCK_CATEGORIES);
  const [reports, setReports] = useState(MOCK_REPORTS);
  const [blocked, setBlocked] = useState([]);
  const [selectedCaregiverId, setSelectedCaregiverId] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState(null);
  const [activeThreadId, setActiveThreadId] = useState("cg_1");
  const [requestFor, setRequestFor] = useState(null);
  const [myCaregiverProfile, setMyCaregiverProfile] = useState(null);
  const [availabilityDays, setAvailabilityDays] = useState({});

  const go = (s) => setScreen(s);
  const viewCaregiver = (id) => {
    setSelectedCaregiverId(id);
    setScreen("profile");
  };

  const runSearch = (criteria) => {
    setSearchCriteria(criteria);
    setScreen("results");
  };

  const sendRequest = (caregiverId, details) => {
    const cg = caregivers.find((c) => c.id === caregiverId);
    setRequests((prev) => [
      {
        id: `req_${Date.now()}`,
        familyKey: viewUser.key === "caregiver" ? "family" : viewUser.key,
        familyName: viewUser.key === "caregiver" ? "Jennifer M." : viewUser.name,
        familyEmail: currentUser?.real ? currentUser.email : "",
        caregiverId,
        status: "sent",
        ...details,
      },
      ...prev,
    ]);
    setRequestFor(null);
    toast.success(`Care request sent to ${cg?.name || "caregiver"}.`);
  };

  const respondRequest = (id, accepted) => {
    const req = requests.find((r) => r.id === id);
    setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status: accepted ? "connected" : "declined" } : r)));
    if (accepted && req) {
      setConnections((prev) =>
        prev.some((c) => c.caregiverId === req.caregiverId)
          ? prev
          : [
              ...prev,
              {
                id: `con_${Date.now()}`,
                caregiverId: req.caregiverId,
                familyKey: req.familyKey,
                familyName: req.familyName,
                connectedDate: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
                status: "connected",
              },
            ]
      );
      // Auto-create a chat thread between the two parties on acceptance
      setThreads((prev) => (prev[req.caregiverId] ? prev : { ...prev, [req.caregiverId]: [] }));
      setActiveThreadId(req.caregiverId);
      setScreen("messages");
      if (currentUser?.real && req.familyEmail) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/threads`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ emails: [req.familyEmail] }),
        })
          .then((r) => (r.ok ? r.json() : null))
          .then((t) => t && toast.success("Secure chat thread opened with the family."))
          .catch(() => {});
      }
      toast.success("Request accepted — you're now connected. Chat thread opened.");
    } else {
      toast("Request declined.");
    }
  };

  const advanceRequest = (id, status) => {
    setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    if (status === "confirmed") toast.success("Arrangement confirmed.");
    if (status === "completed") toast.success("Marked completed — you can now leave a review.");
  };

  const openMessages = (caregiverId) => {
    setThreads((prev) => (prev[caregiverId] ? prev : { ...prev, [caregiverId]: [] }));
    setActiveThreadId(caregiverId);
    setScreen("messages");
  };

  const sendMessage = (caregiverId, text) => {
    if (!text.trim()) return;
    const msg = {
      id: `m_${Date.now()}`,
      sender: "me",
      text: text.trim(),
      time: new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
    };
    setThreads((prev) => ({ ...prev, [caregiverId]: [...(prev[caregiverId] || []), msg] }));
  };

  const reportThread = (caregiverId) => {
    setReports((prev) => [
      ...prev,
      {
        id: `rep_${Date.now()}`,
        caregiverId,
        reason: "Conversation reported by user.",
        status: "open",
        date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      },
    ]);
    toast.success("Conversation reported. Our team will review it.");
  };

  const blockThread = (caregiverId) => {
    setBlocked((prev) => (prev.includes(caregiverId) ? prev.filter((b) => b !== caregiverId) : [...prev, caregiverId]));
  };

  const publishCaregiver = async (profile) => {
    try {
      const res = await fetch(`${API}/api/caregiver/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: profile.name,
          title: profile.title,
          bio: profile.bio,
          phone: profile.phone,
          city: profile.city,
          zip: profile.zip,
          services: profile.services || [],
          categories: profile.categories || [],
          years_experience: profile.yearsExperience || 0,
          experience_details: profile.experienceDetails || "",
          skills: profile.skills || "",
          credentials: (profile.credentials || []).map((c) => (typeof c === "string" ? c : c.type)),
          availability: profile.availability || {},
          radius_miles: profile.radiusMiles || 15,
          hourly_rate: profile.hourlyRate || 0,
          rate_max: profile.rateMax || 0,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || "Could not publish profile.");
        return;
      }
      setMyCaregiverProfile({ ...profile, id: currentUser?.id || `cg_${Date.now()}`, verificationStatus: data.verification_status });
      loadCaregivers();
      toast.success(
        data.verification_status === "verified"
          ? "Profile published to the network — Verified."
          : "Profile published to the network — Unverified until your documents are reviewed."
      );
    } catch {
      toast.error("Network error — could not publish profile.");
    }
  };

  const addReview = (review) => {
    setReviews((prev) => [{ id: `rev_${Date.now()}`, ...review }, ...prev]);
    toast.success("Review submitted. Thank you!");
  };

  const toggleAvailabilityDay = (dayKey) => {
    setAvailabilityDays((prev) => {
      const cur = prev[dayKey];
      const next = cur === "available" ? "unavailable" : cur === "unavailable" ? undefined : "available";
      const copy = { ...prev };
      if (next) copy[dayKey] = next;
      else delete copy[dayKey];
      return copy;
    });
  };

  // Admin actions
  const adminSetStatus = (id, status) => {
    setCaregivers((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
    toast.success(`Caregiver ${status}.`);
  };
  const adminRemove = (id) => {
    setCaregivers((prev) => prev.filter((c) => c.id !== id));
    toast.success("Profile removed.");
  };
  const toggleFeatured = (id) => {
    setCaregivers((prev) => prev.map((c) => (c.id === id ? { ...c, featured: !c.featured } : c)));
  };
  const addCategory = (name) => {
    if (name.trim() && !categories.includes(name.trim())) setCategories((prev) => [...prev, name.trim()]);
  };
  const removeCategory = (name) => setCategories((prev) => prev.filter((c) => c !== name));
  const resolveReport = (id) => setReports((prev) => prev.map((r) => (r.id === id ? { ...r, status: "resolved" } : r)));

  const ctx = {
    viewUser,
    currentUser,
    caregivers,
    requests,
    connections,
    threads,
    reviews,
    categories,
    reports,
    blocked,
    selectedCaregiverId,
    searchCriteria,
    activeThreadId,
    myCaregiverProfile,
    availabilityDays,
    go,
    viewCaregiver,
    runSearch,
    sendRequest,
    respondRequest,
    advanceRequest,
    openMessages,
    sendMessage,
    reportThread,
    blockThread,
    setActiveThreadId,
    publishCaregiver,
    addReview,
    toggleAvailabilityDay,
    adminSetStatus,
    adminRemove,
    toggleFeatured,
    addCategory,
    removeCategory,
    resolveReport,
    openRequestModal: (caregiverId) => setRequestFor(caregiverId),
    onAvatarSync,
  };

  const NAV = [
    { key: "home", label: "Home", icon: Home },
    { key: "find", label: "Find Care", icon: Search },
    { key: "map", label: "Map", icon: MapPin },
    { key: "requests", label: "My Requests", icon: Inbox },
    { key: "connections", label: "My Connections", icon: Users2 },
    { key: "messages", label: "Messages", icon: MessageSquare },
    { key: "caregiving", label: "My Caregiving", icon: Briefcase },
    ...(viewUser.role === "admin" ? [{ key: "admin", label: "Admin", icon: ShieldCheck }] : []),
  ];

  return (
    <div className="min-h-screen" data-testid="cc-shell">
      {/* Sub-header */}
      <div className="border-b border-[#FCE6E9] bg-white/80 backdrop-blur-xl sticky top-0 z-40 dark:bg-[#232D3D]/85 dark:border-[#374457]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={onHome} data-testid="cc-home-logo" title="NestTrack home" className="shrink-0 w-9 h-9 rounded-full border-2 border-[#FCE6E9] overflow-hidden hover:border-[#F8C8DC] transition-colors">
              <img src="/assets/logo.png" alt="NestTrack home" className="w-full h-full object-cover" />
            </button>
            <button
              onClick={onBack}
              data-testid="cc-back-btn"
              className="flex items-center gap-1 pl-2 pr-3 py-1.5 text-[11px] font-bold text-[#9CA3AF] hover:text-[#374151] rounded-full border border-[#FCE6E9] bg-white hover:bg-[#FFF9F2] transition-colors dark:bg-[#273244] dark:border-[#374457]"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Back</span>
            </button>
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-full bg-[#FCE6E9] flex items-center justify-center">
                <HeartHandshake className="w-5 h-5 text-[#E5A0BC]" />
              </span>
              <div>
                <h1 className="font-black text-base font-heading text-[#374151] dark:text-slate-100 leading-tight">Care Connections</h1>
                <p className="hidden sm:block text-[10px] text-[#9CA3AF] font-medium">More than daycare. A connection to care.</p>
              </div>
            </div>
          </div>
          {(currentUser?.role === "master" || (currentUser?.extra_roles || []).includes("master")) && onMasterDash && (
            <button
              onClick={async () => {
                if (currentUser?.role !== "master") {
                  try {
                    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/account/roles/switch`, {
                      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ role: "master" }),
                    });
                  } catch { /* proceed to dashboard regardless */ }
                }
                onMasterDash();
              }}
              data-testid="cc-master-dash-btn"
              title="Back to Master Dashboard"
              className="flex items-center gap-1.5 px-3.5 py-1.5 bg-[#374151] hover:bg-[#1F2937] border border-[#374151] rounded-full text-[11px] font-bold text-white transition-all hover:-translate-y-0.5 shadow-sm"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> Master Dashboard
            </button>
          )}
          {onOpenStore && (
            <button
              onClick={onOpenStore}
              data-testid="cc-store-btn"
              className="flex items-center gap-1.5 px-3.5 py-1.5 bg-white hover:bg-[#E6F2FA] border border-[#B3E0F2] rounded-full text-[11px] font-bold text-[#374151] transition-colors dark:bg-[#273244] dark:border-[#374457] dark:text-slate-200"
            >
              <ShoppingBag className="w-3.5 h-3.5 text-[#5CB3DC]" />
              <span className="hidden sm:inline">Store</span>
            </button>
          )}
          {currentUser && (
            <div className="flex items-center gap-2" data-testid="cc-user-chip">
              <button
                onClick={onEditAvatar}
                data-testid="cc-avatar-btn"
                title="Change my avatar"
                className="w-8 h-8 rounded-full bg-white border-2 border-[#FCE6E9] overflow-hidden flex items-center justify-center shrink-0 shadow-sm hover:border-[#E5A0BC] transition-colors"
              >
                {currentUser.avatar ? (
                  <img src={currentUser.avatar} alt={currentUser.fullName} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs font-black text-[#E5A0BC] font-heading">{currentUser.fullName.charAt(0)}</span>
                )}
              </button>
              <span className="hidden md:block text-xs font-extrabold text-[#374151] dark:text-slate-100">{currentUser.fullName}</span>
              <button
                onClick={onSignOut}
                data-testid="cc-sign-out-btn"
                className="px-3.5 py-1.5 bg-white hover:bg-[#FCE6E9] border border-[#FCE6E9] rounded-full text-[11px] font-bold text-[#6B7280] transition-colors dark:bg-[#273244] dark:border-[#374457] dark:text-slate-300"
              >
                Sign Out
              </button>
            </div>
          )}
        </div>
        {/* Sub nav */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-3 flex gap-2 overflow-x-auto" data-testid="cc-subnav">
          {NAV.map((n) => (
            <button
              key={n.key}
              onClick={() => go(n.key)}
              data-testid={`cc-nav-${n.key}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all duration-200 ${
                screen === n.key || (n.key === "find" && screen === "results")
                  ? "bg-[#F8C8DC] text-[#374151] shadow-sm"
                  : "text-[#9CA3AF] hover:text-[#374151] hover:bg-[#FFF9F2] dark:hover:bg-[#273244]"
              }`}
            >
              <n.icon className="w-3.5 h-3.5" />
              {n.label}
            </button>
          ))}
        </div>
      </div>

      {/* Live Now strip — public live events auto-scroll with manual controls */}
      <LiveNow currentUser={currentUser} page="care" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 flex justify-end" data-testid="care-golive-row">
        <GoLiveButton user={currentUser} location="care" testid="care-go-live" />
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {screen === "home" && <CareHome ctx={ctx} />}
        {screen === "find" && <CareDirectory ctx={ctx} initialTab={initialTab} />}
        {screen === "map" && <CareMap ctx={ctx} />}
        {screen === "results" && <SearchResults ctx={ctx} />}
        {screen === "profile" && <CaregiverProfile ctx={ctx} />}
        {screen === "requests" && <RequestsScreen ctx={ctx} />}
        {screen === "connections" && <ConnectionsScreen ctx={ctx} />}
        {screen === "messages" && <MessagesScreen ctx={ctx} />}
        {screen === "caregiving" && <CaregivingHub ctx={ctx} />}
        {screen === "admin" && viewUser.role === "admin" && <AdminPanel ctx={ctx} />}
      </main>

      {requestFor && (
        <RequestCareModal
          ctx={ctx}
          caregiver={caregivers.find((c) => c.id === requestFor)}
          onClose={() => setRequestFor(null)}
        />
      )}
    </div>
  );
}
